import {
  Box,
  BoxProps,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@chakra-ui/react";
import React, { FC } from "react";

export interface BarEvent {
  start: number;
  end: number;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  color?: string;
  value?: number;
}

export interface DotEvent {
  time: number;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  color?: string;
}

export interface EventsBarProps extends BoxProps {
  events?: BarEvent[];
  dotEvents?: DotEvent[];
  start?: number;
  end?: number;
  blockColor?: string;
  plotAboveAndBelowYAxis?: boolean;
}

const BORDER_WIDTH = "3px";
const BORDER = BORDER_WIDTH + " solid";

export const EventsBar: FC<EventsBarProps> = ({
  events = [],
  dotEvents = [],
  start = 0,
  end = 1,
  blockColor = "blue.300",
  width = "100%",
  height = "50px",
  plotAboveAndBelowYAxis = false,
  ...rest
}) => {
  const valueToPercentage = (value: number) => {
    let fraction = (value - start) / (end - start);
    if (fraction < 0) fraction = 0;
    if (fraction > 1) fraction = 1;
    return (fraction * 100).toFixed(1) + "%";
  };

  const scaleValue = Math.max(
    ...events
      .map((event) => event.value)
      .filter((value): value is number => value !== undefined)
      .map((value) => Math.abs(value))
  );

  const maxValue = Math.max(...events.map((event) => event.value ?? 0));

  return (
    <Box
      borderLeft={BORDER}
      borderRight={BORDER}
      width={width}
      height={height}
      position="relative"
      padding={0}
      {...rest}
    >
      <Box
        position="absolute"
        left={0}
        right={0}
        top={`calc(50% - ${BORDER_WIDTH} / 2)`}
        height={height}
        borderTop={BORDER}
        padding={0}
      />
      {events.map((event, idx) => {
        if (!event.value) return null;
        const sideOrTotalHeight = plotAboveAndBelowYAxis
          ? `calc(${height} / 2 - ${BORDER_WIDTH} / 2)`
          : height;

        return (
          <Popover key={idx}>
            <PopoverTrigger>
              <Box
                position="absolute"
                left={valueToPercentage(event.start)}
                right={valueToPercentage(end - event.end)}
                {...(!plotAboveAndBelowYAxis
                  ? { top: `calc(${(maxValue - (event.value ?? 0)) / (2 * maxValue)} * ${height})` }
                  : { [event.value > 0 ? "bottom" : "top"]: `calc(50% + ${BORDER_WIDTH} / 2)` })}
                height={`calc(${Math.abs(event.value) / scaleValue} * ${sideOrTotalHeight})`}
                background={event.color || blockColor}
                zIndex={1}
                padding={0}
                cursor={event.title || event.content ? "pointer" : undefined}
              />
            </PopoverTrigger>
            {event.title || event.content ? (
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                {event.title ? <PopoverHeader>{event.title}</PopoverHeader> : null}
                {event.content ? <PopoverBody>{event.content}</PopoverBody> : null}
              </PopoverContent>
            ) : null}
          </Popover>
        );
      })}
      {dotEvents.map((dotEvent, idx) => (
        <Popover key={`dot-${idx}`}>
          <PopoverTrigger>
            <Box
              position="absolute"
              left={valueToPercentage(dotEvent.time)}
              marginTop={`calc(${height}/4)`}
              width={1}
              height={`calc(${height}/2)`}
              top={0}
              borderRadius={`calc(${height}/4)`}
              background={dotEvent.color || blockColor}
              borderColor={dotEvent.color || blockColor}
              borderWidth={`calc(${height}/4)`}
              padding={0}
              zIndex={2}
              cursor={dotEvent.title || dotEvent.content ? "pointer" : undefined}
            />
          </PopoverTrigger>
          {dotEvent.title || dotEvent.content ? (
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              {dotEvent.title ? <PopoverHeader>{dotEvent.title}</PopoverHeader> : null}
              {dotEvent.content ? <PopoverBody>{dotEvent.content}</PopoverBody> : null}
            </PopoverContent>
          ) : null}
        </Popover>
      ))}
    </Box>
  );
};
